import { LazyRoute } from "common/routes/route.types";
import { Authorized } from "core/auth/Authorized";
import { bookingRoutes } from "modules/booking/routes";
import { bpMobileRoutes } from "modules/bp-mobile/routes";
import { settingRoutes } from "modules/settings/routes";
import { ComponentType, lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";

import { NotFoundErrorBox, Overlay } from "@bps/fluent-ui";

import { MainLayout } from "./MainLayout";

interface ResolvedLazyRoute
  extends Omit<LazyRoute, "importComponent" | "children"> {
  Component: ComponentType<any>;
  children?: ResolvedLazyRoute[];
}

const lazyRoutes: LazyRoute[] = [bookingRoutes, bpMobileRoutes, settingRoutes];

// Runs once at module load, not on every render
const resolveLazyRoutes = (routes: LazyRoute[]): ResolvedLazyRoute[] =>
  routes.map(({ importComponent, children, ...route }) => ({
    ...route,
    Component: lazy(importComponent),
    children: children ? resolveLazyRoutes(children) : undefined
  }));

const resolvedRoutes = resolveLazyRoutes(lazyRoutes);

const LazyWrapper = ({
  component: Component
}: {
  component: ComponentType<any>;
}) => <Component />;

export const Shell = () => {
  const mapLazyRoutes = (routes: ResolvedLazyRoute[]) => {
    return routes.map(route => {
      const routeProps = {
        path: route.path,
        element: (
          <Authorized
            permissions={route.permissions}
            operator={route.permissionsOperator}
            customPermissionsCheck={route.customPermissionsCheck}
          >
            <LazyWrapper component={route.Component} />
          </Authorized>
        )
      };

      if (route.index) {
        return <Route key={route.id} index {...routeProps} />;
      }

      return (
        <Route
          key={route.id}
          children={route.children ? mapLazyRoutes(route.children) : null}
          {...routeProps}
        />
      );
    });
  };

  return (
    <MainLayout>
      <Suspense fallback={<Overlay styles={{ root: { zIndex: 100 } }} />}>
        <Routes>
          {mapLazyRoutes(resolvedRoutes)}
          <Route path="*" element={<NotFoundErrorBox showLogo />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
};
